import { useEffect, useMemo, useRef, useState } from "react";

import { FirebaseProvider } from "../providers/Firebase";
import { OpenViduProvider } from "../providers/Openvidu";
import { createSession } from "../services/managerService";
import { Roles, EventType, ConnectionStatus } from "../shared/enums";
import { applyInsuredSenderHintsWithRetry, createInsuredPublisherOptions } from "../shared/publisherConfig";
import { getTokenInformation, getVistoriaStatus } from '../services/legacyService';

import {
  Session,
  Publisher,
  StreamEvent,
  Subscriber,
  ConnectionEvent,
  ExceptionEvent,
  SessionDisconnectedEvent,
  StreamPropertyChangedEvent,
  StreamManager,
  VideoElementEvent,
} from "openvidu-browser-v2compatibility";
import { saveEventHistory } from "../services/legacyService";
import { reportError } from "../services/telemetry";

export default function useRTCSession(props: {
  connectionStatus: ConnectionStatus;
  setConnectionStatus: Function;
  sessionId: string;
  isMicActive: boolean;
  setIsMicActive: Function;
  hasVideoAvailable: boolean;
  handleErrorCallback: Function;
  defaultCamera?: MediaDeviceInfo;
  setSubscribers: Function;
  insuranceId?: string;
  userId?: string;
  videoUnavailable?: boolean;
}) {
  const { sessionId, setSubscribers, connectionStatus, setConnectionStatus } = props;

  const [isReady, setReadyness] = useState(false);
  const [session, setSession] = useState<Session>(null!);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);

  const [publisher, setPublisher] = useState<Publisher>(null!);
  const openviduProvider = useMemo(() => new OpenViduProvider(), []);
  const firebaseProvider = useMemo(() => new FirebaseProvider(), []);
  const [recoverException, setRecoverException] = useState<ExceptionEvent | any>(null!);
  const waitingRoomAttempts = useRef(0);
  const MAX_WAITING_ROOM_ATTEMPTS = 24; // ~2 min a 5s entre tentativas

  function subscribeToSessionEvents(session: Session) {
    session.on("streamCreated", (event: StreamEvent) => {
      const { clientData } = JSON.parse(event.stream.connection.data);
      console.log({ clientData, stream: event.stream.streamId });

      if (clientData === Roles.Analyst) {
        const subscriber: Subscriber = session.subscribe(event.stream, undefined!);

        console.info({ subscriber });
        setSubscribers((current: StreamManager[]) => [...current, subscriber]);
      }
    });

    session.on("streamDestroyed", (event: StreamEvent) => {
      const { clientData } = JSON.parse(event.stream.connection.data);
      console.log({ clientData, stream: event.stream.streamId });

      if (clientData === Roles.Analyst) {
        setSubscribers((current: StreamManager[]) => [
          ...current.filter((a) => a.stream.connection.connectionId !== event.stream.connection.connectionId),
        ]);
      }
    });

    session.on("connectionDestroyed", (event: ConnectionEvent) => {
      console.log(`[connectionDestroyed]`, event);
    });

    session.on("connectionCreated", (event: ConnectionEvent) => {
      console.log(`[connectionCreated]`, event.connection.connectionId, event.connection.data);
    });

    // https://docs.openvidu.io/en/stable/api/openvidu-browser-v2compatibility/classes/SessionDisconnectedEvent.html
    session.on("sessionDisconnected", (event: SessionDisconnectedEvent) => {
      console.log(`[sessionDisconnected]`, event);

      // Evita redirecionamento duplo: só redireciona se não estiver já na tela de feedback
      if (window.location.pathname.includes('/feedback')) {
        return;
      }

      if (event.reason === "networkDisconnect") {
        saveEventHistory(`Segurado: Perda de conexão de rede`, sessionId);

        console.log({ isRecovering, recoverException, publisher });

        setIsRecovering(true);
        setRecoverException({
          name: "ApplicationCustomError",
          message: "Segurado: Perda de conexão de rede",
          origin: "sessionDisconnected.networkDisconnect",
        });
      }
    });

    session.on(ConnectionStatus.RECONNECTING, () => {
      setIsReconnecting(true);
      setConnectionStatus(false);
      console.log(`[reconnecting]`);
      saveEventHistory(`Segurado: Reconectando à sessão`, sessionId);
    });

    session.on(ConnectionStatus.RECONNECTED, () => {
      setIsReconnecting(false);
      setConnectionStatus(true);
      console.log(`[reconnected]`);
      saveEventHistory(`Segurado: Conexão à sessão restabelecida`, sessionId);
    });

    session.on("streamPropertyChanged", (event: StreamPropertyChangedEvent) => {
      console.log("[streamPropertyChanged]", {
        event,
      });
    });

    session.on("exception", async (exception: ExceptionEvent) => {
      console.warn({ exception, isReconnecting });

      switch (exception.name) {
        case "ICE_CANDIDATE_ERROR":
          setRecoverException(exception);
          break;
        case "ICE_CONNECTION_FAILED":
          setRecoverException(exception);
          setIsRecovering(true);
          break;
        case "ICE_CONNECTION_DISCONNECTED":
          setRecoverException(exception);
          setIsRecovering(true);
          break;
        case "NO_STREAM_PLAYING_EVENT":
          setRecoverException(exception);
          break;
      }

      void reportError("de conexão (ICE/sessão)", exception, sessionId, { dedupe: true });

      firebaseProvider.sendUserEvent(EventType.EXCEPTION, {
        description: `${exception.name}|${exception.message}`,
        fatal: true,
      });
    });
  }

  async function connectSession(session: Session, publisher: Publisher) {
    try {
      // 1. Validação inicial do token
      await getTokenInformation(sessionId);

      // 2. Consulta Status na API Mobi (Fonte da Verdade)
      const statusData = await getVistoriaStatus(sessionId);
      const closedStatuses = [3, 5, 6, 7, 10];

      if (statusData && closedStatuses.includes(Number(statusData.STATUS))) {
        console.warn("Vistoria já concluída no Mobi. Redirecionando para feedback...");

        // Passando parâmetros para evitar undefined no Feedback/CSAT
        const params = new URLSearchParams();
        params.append('isFinished', 'true');
        params.append('token', sessionId);

        if (props.insuranceId)
          params.append('ins', props.insuranceId);

        if (props.userId)
          params.append('usr', props.userId);

        window.location.href = `/feedback?${params.toString()}`;
        return;
      }

      // 3. Trava de reingresso único
      if (isReady) return;

      const { token } = await createSession(sessionId);

      await session.connect(token, { clientData: Roles.Insured });
      await session.publish(publisher);
      void applyInsuredSenderHintsWithRetry(publisher);
      setReadyness(true);

      firebaseProvider.sendUserEvent(EventType.LEVEL_START, {
        level_name: "Ingresso à sala de vídeo estabelecido",
      });

    } catch (error: any) {
      console.error("Erro na tentativa de conexão RTC:", error);

      const isWaitingRoom =
        error.status === 404 ||
        error.message?.includes("404") ||
        error.name === "OPENVIDU_ENDPOINT_NOT_FOUND";

      if (isWaitingRoom && !isReady) {
        if (waitingRoomAttempts.current >= MAX_WAITING_ROOM_ATTEMPTS) {
          void reportError("sala não iniciada (timeout de espera)", error, sessionId);
          props.handleErrorCallback(error);
          return;
        }
        waitingRoomAttempts.current += 1;
        console.log(`Aguardando perito iniciar a sala... tentativa ${waitingRoomAttempts.current}/${MAX_WAITING_ROOM_ATTEMPTS}`);

        setTimeout(() => {
          if (!isReady) connectSession(session, publisher);
        }, 5000);

      } else {
        void reportError("ao publicar/conectar na sala", error, sessionId);
        props.handleErrorCallback(error);
      }
    }
  }

  useEffect(() => {
    if (!publisher && (props.defaultCamera || props.videoUnavailable)) {
      const deviceConfig = props.videoUnavailable
        ? createInsuredPublisherOptions({ publishVideo: false, videoSource: false })
        : createInsuredPublisherOptions({ videoSource: props.defaultCamera?.deviceId });

      const pub = openviduProvider.initializePublisher("local-media", deviceConfig, props.handleErrorCallback);

      pub.on("videoElementCreated", (event: VideoElementEvent) => {
        console.log(`publisher.videoElementCreated`, event);
        console.log(`Segurado: Tela de transmissão de vídeo criada`);
      });

      pub.on("videoElementDestroyed", (event: VideoElementEvent) => {
        console.log(`publisher.videoElementDestroyed`, event);
        console.log(`Segurado: Tela de transmissão de vídeo removida`);
      });

      setPublisher(pub);
    }
  }, [publisher, props.defaultCamera]);

  useEffect(() => {
    if (connectionStatus === ConnectionStatus.CONNECTED && publisher && !session) {
      try {
        const session = openviduProvider.initializeSession();
        subscribeToSessionEvents(session);

        setSession(session);
        setIsConnecting(true);
      } catch (error: any) {
        props.handleErrorCallback(error);
      }
    }
  }, [publisher, session, connectionStatus]);

  useEffect(() => {
    if (isConnecting && session && publisher) {
      const connect = async () => {
        try {
          await connectSession(session, publisher);
          setIsConnecting(false);
        } catch (error) {
          props.handleErrorCallback(error);
        }
      };

      connect();
    }
  }, [isConnecting, session, publisher]);

  useEffect(() => {
    if (recoverException && isRecovering && connectionStatus === ConnectionStatus.CONNECTED) {
      let cancelled = false;

      const timer = setTimeout(async () => {
        try {
          await connectSession(session, publisher);
        } finally {
          if (!cancelled) {
            setRecoverException(null);
            setIsRecovering(false);
          }
        }
      }, 1500);

      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }
  }, [recoverException, isRecovering, connectionStatus]);

  return {
    isReady,
    session,
    publisher,
    isRecovering,
    setPublisher,
    recoverException,
    openviduProvider,
  };
}